import {
  OneDayInMillis,
  OneHourInMillis,
  OneMinuteInMillis,
  OneSecondInMillis,
} from '@racemap/utilities/consts/time';
import { clamp } from '@racemap/utilities/math';
import { useDrag, useGesture } from '@use-gesture/react';
import range from 'lodash/range';
import { type JSX, useEffect, useRef, useState } from 'react';
import type { Tuple2 } from 'src/lib/player/constants';
import type { TimeMode } from 'src/lib/player/store';
import {
  ceilTime,
  ceilTimeAbsolute,
  floorTime,
  floorTimeAbsolute,
  leftpad2,
  NBSP,
} from 'src/lib/player/utils';

type StrideDefinition = { primary: number; secondary: number };

const HANDLE_PADDING = 2 * 9;
const TICK_WIDTH = 100;
const STRIDES: Array<StrideDefinition> = [
  { primary: 30 * OneSecondInMillis, secondary: 5 * OneSecondInMillis },
  { primary: OneMinuteInMillis, secondary: 15 * OneSecondInMillis },
  { primary: 5 * OneMinuteInMillis, secondary: OneMinuteInMillis },
  { primary: 10 * OneMinuteInMillis, secondary: 2 * OneMinuteInMillis },
  { primary: 15 * OneMinuteInMillis, secondary: 5 * OneMinuteInMillis },
  { primary: 30 * OneMinuteInMillis, secondary: 5 * OneMinuteInMillis },
  { primary: OneHourInMillis, secondary: 15 * OneMinuteInMillis },
  { primary: 2 * OneHourInMillis, secondary: 30 * OneMinuteInMillis },
  { primary: 6 * OneHourInMillis, secondary: OneHourInMillis },
  { primary: 12 * OneHourInMillis, secondary: 2 * OneHourInMillis },
  { primary: OneDayInMillis, secondary: 6 * OneHourInMillis },
  { primary: 3 * OneDayInMillis, secondary: 12 * OneHourInMillis },
  { primary: 7 * OneDayInMillis, secondary: OneDayInMillis },
  { primary: 14 * OneDayInMillis, secondary: 3 * OneDayInMillis },
  { primary: 28 * OneDayInMillis, secondary: 7 * OneDayInMillis },
  { primary: 70 * OneDayInMillis, secondary: 14 * OneDayInMillis },
  { primary: 140 * OneDayInMillis, secondary: 35 * OneDayInMillis },
  { primary: 365 * OneDayInMillis, secondary: 73 * OneDayInMillis },
];

function findGranularities(sliderDuration: number, stride: number): [number, number, number] {
  let maxGranularity = OneSecondInMillis;
  if (sliderDuration >= OneMinuteInMillis) {
    maxGranularity = OneMinuteInMillis;
  }
  if (sliderDuration >= OneHourInMillis) {
    maxGranularity = OneHourInMillis;
  }
  if (sliderDuration >= OneDayInMillis) {
    maxGranularity = OneDayInMillis;
  }

  let minGranularity = OneSecondInMillis;
  if (stride >= OneMinuteInMillis) {
    minGranularity = OneMinuteInMillis;
  }
  if (stride >= OneHourInMillis) {
    minGranularity = OneHourInMillis;
  }
  if (stride >= OneDayInMillis) {
    minGranularity = OneDayInMillis;
  }

  let nextGranularity = OneSecondInMillis;
  if (minGranularity === OneSecondInMillis) {
    nextGranularity = OneMinuteInMillis;
  }
  if (minGranularity === OneMinuteInMillis) {
    nextGranularity = OneHourInMillis;
  }
  if (minGranularity === OneHourInMillis) {
    nextGranularity = OneDayInMillis;
  }
  return [minGranularity, nextGranularity, maxGranularity];
}

function findTicksRelative(
  visibleMin: number,
  visibleMax: number,
  sliderMin: number,
  sliderMax: number,
  count: number,
  tickStrides: Array<StrideDefinition>,
): {
  sticky: Array<{ value: number; label: string }>;
  primary: Array<{ value: number; label: string }>;
  secondary: Array<number>;
  granularity: [number, number];
} {
  let stride = tickStrides[0];
  for (const _stride of tickStrides) {
    if ((visibleMax - visibleMin) / _stride.primary < count) {
      stride = _stride;
      break;
    }
  }

  const ticksPrimaryValues = range(
    floorTime(visibleMin - sliderMin, stride.primary),
    ceilTime(visibleMax - sliderMin, stride.primary) + 1,
    stride.primary,
  ).map((t) => t + sliderMin);
  const ticksSecondaryValues = range(
    floorTime(visibleMin - sliderMin, stride.secondary),
    ceilTime(visibleMax - sliderMin, stride.secondary) + 1,
    stride.secondary,
  )
    .map((t) => t + sliderMin)
    .filter((tick) => !ticksPrimaryValues.includes(tick));

  const [minGranularity, nextGranularity, maxGranularity] = findGranularities(
    sliderMax - sliderMin,
    stride.primary,
  );

  const ticksSticky =
    minGranularity === maxGranularity
      ? ticksPrimaryValues.map((t) => ({
          value: t,
          label: formatStickyTickLabelRelative(t - sliderMin, minGranularity, maxGranularity),
        }))
      : range(
          floorTime(visibleMin - sliderMin, nextGranularity),
          ceilTime(visibleMax - sliderMin, nextGranularity) + 1,
          nextGranularity,
        ).map((t) => ({
          value: t + sliderMin,
          label: formatStickyTickLabelRelative(t, minGranularity + 1, maxGranularity),
        }));

  return {
    sticky: ticksSticky,
    primary:
      minGranularity === maxGranularity
        ? []
        : ticksPrimaryValues.map((t) => ({
            value: t,
            label: formatTickLabelRelative(t - sliderMin, minGranularity),
          })),
    secondary: ticksSecondaryValues,
    granularity: [minGranularity, maxGranularity],
  };
}

function findTicksAbsolute(
  visibleMin: number,
  visibleMax: number,
  sliderMin: number,
  sliderMax: number,
  count: number,
  tickStrides: Array<StrideDefinition>,
): {
  sticky: Array<{ value: number; label: string }>;
  primary: Array<{ value: number; label: string }>;
  secondary: Array<number>;
  granularity: [number, number];
} {
  let stride = tickStrides[0];
  for (const _stride of tickStrides) {
    if ((visibleMax - visibleMin) / _stride.primary < count) {
      stride = _stride;
      break;
    }
  }

  const sliderFloorTime = floorTimeAbsolute(sliderMin, stride.primary);
  const sliderCeilTime = ceilTimeAbsolute(sliderMax, stride.primary);

  const visibleFloorTime =
    sliderFloorTime + Math.floor((visibleMin - sliderFloorTime) / stride.primary) * stride.primary;
  const visibleCeilTime =
    sliderCeilTime - Math.floor((sliderCeilTime - visibleMax) / stride.primary) * stride.primary;

  const ticksPrimaryValues = range(visibleFloorTime, visibleCeilTime + 1, stride.primary);
  const ticksSecondaryValues = range(
    visibleFloorTime,
    visibleCeilTime + 1,
    stride.secondary,
  ).filter((tick) => !ticksPrimaryValues.includes(tick));

  const [minGranularity, nextGranularity, maxGranularity] = findGranularities(
    sliderMax - sliderMin,
    stride.primary,
  );

  const ticksSticky =
    minGranularity === maxGranularity
      ? ticksPrimaryValues.map((t) => ({
          value: t,
          label: formatStickyTickLabelAbsolute(t, minGranularity),
        }))
      : range(
          floorTimeAbsolute(visibleMin, nextGranularity),
          ceilTimeAbsolute(visibleMax, nextGranularity),
          nextGranularity,
        ).map((t) => ({
          value: t,
          label: formatStickyTickLabelAbsolute(t, minGranularity + 1),
        }));

  return {
    sticky: ticksSticky,
    primary:
      minGranularity === maxGranularity
        ? []
        : ticksPrimaryValues.map((t) => ({
            value: t,
            label: formatTickLabelAbsolute(t, minGranularity),
          })),
    secondary: ticksSecondaryValues,
    granularity: [minGranularity, maxGranularity],
  };
}

function formatHandleLabelAbsolute(time: number): JSX.Element {
  const date = new Date(time);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const min = date.getMinutes();
  const hour = date.getHours();
  const sec = date.getSeconds();

  return (
    <>
      {year}-{leftpad2(month)}-{leftpad2(day)}
      <br />
      {leftpad2(hour)}h{NBSP}
      {leftpad2(min)}m{NBSP}
      {leftpad2(sec)}s
    </>
  );
}

function formatHandleLabelRelative(time: number, showDays: boolean): string {
  const sec = Math.floor(time / OneSecondInMillis);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);

  return showDays
    ? `${leftpad2(day)}d${NBSP}${leftpad2(hour % 24)}h${NBSP}${leftpad2(
        min % 60,
      )}m${NBSP}${leftpad2(sec % 60)}s`
    : `${leftpad2(hour)}h${NBSP}${leftpad2(min % 60)}m${NBSP}${leftpad2(sec % 60)}s`;
}

function formatTickLabelRelative(time: number, granularity: number): string {
  const seconds = Math.floor(time / OneSecondInMillis);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  switch (granularity) {
    case OneSecondInMillis:
      return `${leftpad2(seconds % 60)}s`;
    case OneMinuteInMillis:
      return `${leftpad2(minutes % 60)}m`;
    case OneHourInMillis:
      return `${leftpad2(hours % 24)}h`;
    case OneDayInMillis:
      return `${leftpad2(days)}d`;
    default:
      return '-';
  }
}

function formatStickyTickLabelRelative(
  time: number,
  minGranularity: number,
  maxGranularity: number,
): string {
  const seconds = Math.floor(time / OneSecondInMillis);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const output: Array<string> = [];
  if (minGranularity <= OneDayInMillis && OneDayInMillis <= maxGranularity) {
    output.push(`${leftpad2(days)}d`);
  }
  if (minGranularity <= OneHourInMillis && OneHourInMillis <= maxGranularity) {
    output.push(`${leftpad2(hours % 24)}h`);
  }
  if (minGranularity <= OneMinuteInMillis && OneMinuteInMillis <= maxGranularity) {
    output.push(`${leftpad2(minutes % 60)}m`);
  }
  if (minGranularity <= OneSecondInMillis && OneSecondInMillis <= maxGranularity) {
    output.push(`${leftpad2(seconds % 60)}s`);
  }
  return output.join(' ');
}

function formatTickLabelAbsolute(time: number, granularity: number): string {
  const date = new Date(time);

  switch (granularity) {
    case OneSecondInMillis:
      return `${leftpad2(date.getSeconds())}s`;
    case OneMinuteInMillis:
      return `${leftpad2(date.getMinutes())}m`;
    case OneHourInMillis:
      return `${leftpad2(date.getHours())}h`;
    case OneDayInMillis:
      return leftpad2(date.getDate());
    default:
      return '-';
  }
}

function formatStickyTickLabelAbsolute(time: number, minGranularity: number): string {
  const date = new Date(time);
  const output: Array<string> = [];
  if (minGranularity <= OneDayInMillis) {
    output.push(
      `${date.getFullYear()}-${leftpad2(date.getMonth() + 1)}-${leftpad2(date.getDate())}`,
    );
  }
  if (minGranularity <= OneHourInMillis) {
    output.push(`${leftpad2(date.getHours())}h`);
  }
  if (minGranularity <= OneMinuteInMillis) {
    output.push(`${leftpad2(date.getMinutes())}m`);
  }
  if (minGranularity <= OneSecondInMillis) {
    output.push(`${leftpad2(date.getSeconds())}s`);
  }
  return output.join(' ');
}

function calcMinZoom(timeDuration: number, min: number, max: number): number {
  return Math.log(-timeDuration / (min - max)) / Math.log(2);
}

export type SliderProps = {
  value: number;
  min: number;
  max: number;
  timeMode: TimeMode;
  shade?: Array<Tuple2>;
  onChange: (newValue: number) => void;
  /** Once per drag, on release. */
  onChangeEnd?: () => void;
};

export default function Slider({
  min,
  max,
  value,
  timeMode,
  shade = [],
  onChange,
  onChangeEnd,
}: SliderProps): JSX.Element {
  const [zoom, setZoom] = useState(calcMinZoom(30 * OneMinuteInMillis, min, max));
  const expZoom = 1 / 2 ** zoom;

  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderSliderRef = useRef<HTMLDivElement>(null);
  const sliderHandleRef = useRef<HTMLDivElement>(null);
  const sliderTickRef = useRef<HTMLDivElement>(null);

  const visibleRangeStart = min + (value - min) * ((expZoom - 1) / expZoom);
  const visibleRangeEnd = visibleRangeStart + (max - min) / expZoom;
  const sliderWidth = sliderRef.current?.offsetWidth ?? 0;
  const handleWidth = sliderHandleRef.current?.offsetWidth ?? 0;
  const optimalTickCount = sliderWidth / TICK_WIDTH;
  const {
    primary: ticksPrimary,
    secondary: ticksSecondary,
    sticky: ticksSticky,
    granularity: [, tickMaxGranularity],
  } = timeMode === 'RELATIVE'
    ? findTicksRelative(visibleRangeStart, visibleRangeEnd, min, max, optimalTickCount, STRIDES)
    : findTicksAbsolute(visibleRangeStart, visibleRangeEnd, min, max, optimalTickCount, STRIDES);

  useGesture(
    {
      onDrag: ({ pinching, down, last, delta: [deltaX], cancel }) => {
        if (pinching) {
          cancel();
          return;
        }
        if (last) onChangeEnd?.();
        if (down) {
          if (sliderRef.current == null) return;

          onChange(
            clamp(
              min,
              value - (deltaX / (sliderRef.current.offsetWidth * expZoom)) * (max - min),
              max,
            ),
          );
        }
      },
      onPinch: ({ delta: [deltaX], offset: [offsetX] }) => {
        const minZoom = calcMinZoom(10 * OneMinuteInMillis, min, max);
        setZoom((zoom) => clamp(minZoom, zoom - deltaX / offsetX, 0));
      },
      onWheel: ({ ctrlKey, delta: [, deltaY] }) => {
        if (!ctrlKey) {
          const minZoom = calcMinZoom(10 * OneMinuteInMillis, min, max);
          setZoom((zoom) => clamp(minZoom, zoom + deltaY / 1000, 0));
        }
      },
    },
    { target: sliderTickRef },
  );
  useDrag(
    ({ down, last, delta: [deltaX] }) => {
      if (last) onChangeEnd?.();
      if (down) {
        onChange(
          clamp(
            min,
            value + (deltaX / (sliderWidth - (handleWidth - HANDLE_PADDING))) * (max - min),
            max,
          ),
        );
      }
    },
    { target: sliderSliderRef },
  );

  useEffect(() => {
    if (sliderRef.current == null) return;
    setZoom(
      calcMinZoom(clamp(30, sliderRef.current.offsetWidth / 10, 120) * OneMinuteInMillis, min, max),
    );
  }, []);

  useEffect(() => {
    const handler = (e: Event) => e.preventDefault();
    const slider = sliderRef.current;
    if (slider == null) return;

    slider.addEventListener('touchstart', handler);
    return () => {
      slider.removeEventListener('touchstart', handler);
    };
  }, []);

  function calcOffsetFraction(v: number) {
    return (v - min) / (max - min);
  }
  function calcZoomedOffsetFraction(v: number) {
    return (expZoom * v - expZoom * value + value - min) / (max - min);
  }

  return (
    <div className="slider" ref={sliderRef}>
      <div className="slider-tick-wrapper" ref={sliderTickRef}>
        {ticksSticky.map((tick, i) => (
          <div
            key={`sticky${tick.value}`}
            className="slider-tick-sticky"
            style={{
              transform: `translateX(${clamp(0, calcZoomedOffsetFraction(tick.value) * sliderWidth, sliderWidth - 1)}px)`,
              zIndex: 200 + i,
            }}
          >
            <span>{tick.label}</span>
          </div>
        ))}
        {ticksPrimary.map((tick, i) => (
          <div
            key={`primary${tick.value}`}
            className="slider-tick-primary"
            style={{
              zIndex: 100 + i,
              transform: `translateX(${clamp(Number.NEGATIVE_INFINITY, calcZoomedOffsetFraction(tick.value) * sliderWidth, sliderWidth)}px)`,
            }}
          >
            <span>{tick.label}</span>
          </div>
        ))}
        {ticksSecondary.map((tick) => (
          <div
            key={`secondary${tick}`}
            className="slider-tick-secondary"
            style={{
              transform: `translateX(${clamp(0, calcZoomedOffsetFraction(tick) * sliderWidth, sliderWidth)}px)`,
            }}
          />
        ))}
      </div>
      <div className="slider-slider" ref={sliderSliderRef}>
        <div
          className="slider-handle"
          translate="no"
          ref={sliderHandleRef}
          style={{
            width:
              tickMaxGranularity === OneDayInMillis && timeMode === 'RELATIVE' ? 90 : undefined,
            transform: `translateX(${
              ((value - min) / (max - min)) * (sliderWidth - (handleWidth - HANDLE_PADDING))
            }px)`,
          }}
        >
          <div
            className="slider-handle-arrow"
            style={{
              transform: `translateX(${
                ((value - min) / (max - min)) * (handleWidth - HANDLE_PADDING)
              }px)`,
            }}
          />
          {timeMode === 'ABSOLUTE'
            ? formatHandleLabelAbsolute(value)
            : formatHandleLabelRelative(value - min, tickMaxGranularity === OneDayInMillis)}
        </div>
        <div
          className="slider-track"
          style={{
            left: 0,
            width: calcOffsetFraction(value) * sliderWidth,
          }}
        />
        {shade.map(([shadeMin, shadeMax]) => (
          <div
            key={`shade-${shadeMin}-${shadeMax}`}
            className="slider-shade"
            style={{
              left: calcOffsetFraction(shadeMin) * sliderWidth,
              width: (calcOffsetFraction(shadeMax) - calcOffsetFraction(shadeMin)) * sliderWidth,
            }}
          />
        ))}
        <div className="slider-background" />
      </div>
    </div>
  );
}
